/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'

import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import ReactFullpage from '@fullpage/react-fullpage'
import { PhotoSlide } from 'components/photoslide'
import './styles.scss'

const ARTICLE_ID = '718510005243'

const slidesContent = [
  {
    imgDesktop: '01.jpg',
    imgMobile: '01-mobile.jpg',
    caption:
      'Einwohner von Kiew nutzen eine unterirdische U-Bahn-Station als Bombenschutzraum.',
    credits: 'Foto: Aris Messinis, STF/AFP'
  },
  {
    imgDesktop: '02.jpg',
    imgMobile: '02-mobile.jpg',
    caption:
      'Sergyi Badylevych mit seiner Tochter im Untergrund der Kiewer U-Bahn.',
    credits: 'Foto: Aris Messinis, STF/AFP'
  },
  {
    imgDesktop: '03.jpg',
    imgMobile: '03-mobile.jpg',
    caption:
      'Auch dieses Paar hat sich in der U-Bahn der Hauptstadt mit Decken und Matratzen ein notdürftiges Lager eingerichtet.',
    credits: 'Foto: Aris Messinis, STF/AFP'
  },
  {
    imgDesktop: '04.jpg',
    imgMobile: '04-mobile.jpg',
    caption:
      'Eine Mutter verpflegt ihre Kinder in einer Station der tiefgelegenen Kiewer U-Bahn.',
    credits: 'Foto: Emilio Morenatti, AP Photo'
  },
  {
    imgDesktop: '05.jpg',
    imgMobile: '05-mobile.jpg',
    caption:
      'Eine Frau hat mit ihrem Sohn auf einer Luftmatratze in der Tiefgarage eines grossen Wohnblocks in Kiew.',
    credits: 'Chris McGrath, Getty Images'
  },
  {
    imgDesktop: '06.jpg',
    imgMobile: '06-mobile.jpg',
    caption:
      'Auch diese Frau hat in der Tiefgarage eines Kiewer Wohnblocks Zuflucht gefunden.',
    credits: 'Chris McGrath, Getty Images'
  },
  {
    imgDesktop: '07.jpg',
    imgMobile: '07-mobile.jpg',
    caption:
      'Ältere Menschen suchen Schutz im Keller eines Gebäudes in Charkiw mit einigen Habseligkeiten und Lebensmitteln, nachdem russische Truppen das Gebiet der zweitgrössten ukrainischen Stadt beschossen haben.',
    credits: 'Foto: Sergey Bobok, AFP'
  },
  {
    imgDesktop: '08.jpg',
    imgMobile: '08-mobile.jpg',
    caption: 'Ausharren in einem Luftschutzkeller in Kiew.',
    credits: 'Foto: Alex Lourie, Redux/Laif'
  },
  {
    imgDesktop: '09.jpg',
    imgMobile: '09-mobile.jpg',
    caption:
      'Eine Frau schläft in der Tiefgarage eines Hotels, die während eines Luftangriffsalarms in Kiew zu einem Luftschutzkeller umfunktioniert wurde.',
    credits: 'Foto: Vadim Ghirda, AP Photo'
  },
  {
    imgDesktop: '10.jpg',
    imgMobile: '10-mobile.jpg',
    caption:
      'Diese Garage eines Kiewer Hotels bietet jungen Menschen Schutz. Das Smartphone dient als Kontakt zur Aussenwelt.',
    credits: 'Foto: Vadim Ghirda, AP Photo'
  },
  {
    imgDesktop: '11.jpg',
    imgMobile: '11-mobile.jpg',
    caption: 'Notdürftiger Bombenschutz: Ein Mädchen in Mariupol.',
    credits: 'Foto: Evgeniy Maloletka, AFP'
  }
]

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      isMobile: window.innerWidth < 800,
      slideTransition: 'slide', // slide|fade
      activeSlide: 0,
      captionOpen: false,
      captionWasOpen: false,
      photoSlides: []
    }
    this.onLeave = this.onLeave.bind(this)
    this.afterLoad = this.afterLoad.bind(this)
    this.toggleCaption = this.toggleCaption.bind(this)
    this.updateDimensions = this.updateDimensions.bind(this)
  }

  updateDimensions() {
    // console.log('updateDimensions')
    this.setState({ isMobile: window.innerWidth < 800 })
  }

  onLeave(index, nextIndex, direction) {
    // console.log('onLeave')
    this.setState({ captionWasOpen: this.state.captionOpen })
    this.setState({ isSliding: true })
    this.setState({ captionOpen: false })

    // console.log('nextIndex: ', nextIndex.index)
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'scroll',
      event_label: `scrolled to: ${nextIndex.index}`
    })
  }

  afterLoad(anchorLink, index, slideAnchor, slideIndex) {
    // console.log('afterLoad')
    this.setState({ isSliding: false })
    // console.log('active index: ', index.index)
    this.setState({ activeSlide: index.index })
    if (!this.state.isMobile) {
      this.toggleCaption()
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)
  }

  toggleCaption() {
    // console.log('toggleCaption')
    this.setState({ captionOpen: !this.state.captionOpen })
    if (this.state.isMobile && !this.state.captionOpen) {
      // console.log('click toggle -> TRACK: ', this.state.activeSlide)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'click',
        event_label: `show caption: ${this.state.activeSlide}`
      })
    }
  }

  render() {
    const photoSlides = slidesContent.map(
      function(slide, index) {
        // console.log('slide', slide)
        return (
          <>
            <PhotoSlide
              id={'section-' + (index + 1)}
              isMobile={this.state.isMobile}
              imgMobile={slide.imgMobile}
              imgDesktop={slide.imgDesktop}
              isSliding={this.state.isSliding}
              captionOpen={this.state.captionOpen}
              captionWasOpen={this.state.captionWasOpen}
              toggleCaption={this.toggleCaption}
              caption={slide.caption}
              credits={slide.credits}
            />
          </>
        )
      }.bind(this)
    )

    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} inverted />

        <ReactFullpage
          onLeave={this.onLeave}
          afterLoad={this.afterLoad}
          navigation
          navigationPosition='right'
          navigationTooltips={['', '', '']}
          licenseKey='9B91FA5D-FFD748B8-88A4F827-CEF667F2'
          render={({ state, fullpageApi }) => {
            return (
              <ReactFullpage.Wrapper>
                <ThemeSwitch />
                <div
                  className='section'
                  id='section-0'
                  style={{
                    backgroundImage: `url(${
                      this.state.isMobile
                        ? require('../../images/00-mobile.jpg')
                        : require('../../images/00.jpg')
                    })`
                  }}
                >
                  <h1>Leben im Untergrund</h1>
                  <p className='lead'>
                    Keller, Garagen, Bunker und U-Bahn-Stationen: Wie Ukrainer
                    unter der Erde Schutz vor Putins Raketen suchen.
                  </p>
                  <div className='scroll-hint'>
                    <p style={{ textAlign: 'center' }}>
                      Bitte scrollen
                      <br />
                      <i className='icon angle down huge' />
                    </p>
                  </div>
                </div>

                {photoSlides}

                <div
                  className='section'
                  id='section-footer'
                  style={{ padding: 0 }}
                >
                  <Footer
                    articleId={ARTICLE_ID}
                    invertedTop
                    invertedBottom
                    hidePortfolioTeaser
                    questions={[
                      'Wie informativ war der Beitrag?',
                      'Wie gut ist die Bedienbarkeit der Bildstrecke?'
                    ]}
                    credits={[
                      ['Konzept und Umsetzung', 'Marc Brupbacher, Mathias Lutz']
                    ]}
                  >
                    <div className='ui text container'>
                      <Link
                        href={
                          'https://www.' +
                          parseHostname().publicationDomain +
                          '/fokusthema'
                        }
                        target='_top'
                        allowWordWrap
                        hideIcon
                      >
                        Mehr zum Thema Ukraine-Krieg
                      </Link>
                    </div>
                  </Footer>
                </div>
              </ReactFullpage.Wrapper>
            )
          }}
        />
      </div>
    )
  }
}
